// Boost Blenz Admin — shared atoms: icons, text, cards, badges, buttons, charts.
// Ported verbatim from the prototype's admin-atoms.jsx. Three themes via ThemeContext.

import React from "react";
import { OSWALD, FIGTREE, useT } from "./theme";
import { BBAdminData } from "./data/admin";

// ---------------- ICONS (simple line glyphs) ----------------
export function Icon({ name, size = 18, color = 'currentColor', strokeWidth = 1.8, style = {} }: any) {
  const p: any = { fill: 'none', stroke: color, strokeWidth, strokeLinecap: 'round', strokeLinejoin: 'round' };
  const paths: any = {
    health: <><path d="M3 12h3l2 5 4-12 2 7h2" {...p} /></>,
    users: <><circle cx="8" cy="8" r="3" {...p} /><path d="M2.5 19a5.5 5.5 0 0 1 11 0" {...p} /><path d="M16 6a3 3 0 0 1 0 6M16.5 19a5.5 5.5 0 0 0-3-4.9" {...p} /></>,
    bell: <><path d="M6 9a6 6 0 0 1 12 0c0 5 2 6 2 6H4s2-1 2-6Z" {...p} /><path d="M10 20a2 2 0 0 0 4 0" {...p} /></>,
    doc: <><path d="M6 3h8l4 4v14H6Z" {...p} /><path d="M14 3v4h4" {...p} /><path d="M9 12h6M9 16h6" {...p} /></>,
    mute: <><path d="M4 9v6h4l5 4V5L8 9Z" {...p} /><path d="M17 9l4 6M21 9l-4 6" {...p} /></>,
    search: <><circle cx="10.5" cy="10.5" r="6" {...p} /><path d="m20 20-4.5-4.5" {...p} /></>,
    check: <><path d="M5 12.5 10 17l9-10" {...p} /></>,
    dot: <><circle cx="12" cy="12" r="4" fill={color} stroke="none" /></>,
    alert: <><path d="M12 4 2.5 20h19L12 4Z" {...p} /><path d="M12 10v4M12 17v.5" {...p} /></>,
    arrowUp: <><path d="M12 19V5M6 11l6-6 6 6" {...p} /></>,
    arrowDown: <><path d="M12 5v14M6 13l6 6 6-6" {...p} /></>,
    chevron: <><path d="m9 6 6 6-6 6" {...p} /></>,
    x: <><path d="M6 6l12 12M18 6 6 18" {...p} /></>,
    image: <><rect x="3" y="5" width="18" height="14" rx="2" {...p} /><circle cx="8.5" cy="10" r="1.5" {...p} /><path d="m4 17 5-5 4 4 3-2 4 3" {...p} /></>,
    send: <><path d="M4 12 20 4l-6 16-3-7-7-1Z" {...p} /></>,
    refresh: <><path d="M4 12a8 8 0 0 1 13.7-5.7L20 8M20 4v4h-4" {...p} /><path d="M20 12a8 8 0 0 1-13.7 5.7L4 16M4 20v-4h4" {...p} /></>,
    pulse: <><path d="M3 12h4l2 6 4-14 2 8h6" {...p} /></>,
    // chart = axes + rising trend line (data/analytics glyph)
    chart: <><path d="M4 4v16h16" {...p} /><path d="m8 15 3-4 3 2 4-6" {...p} /></>,
    // grid = table/spreadsheet (Decision Dashboard)
    grid: <><rect x="3" y="3" width="18" height="18" rx="2" {...p} /><path d="M3 9h18M3 15h18M9 3v18" {...p} /></>,
    // funnel = filter affordance (Decision Dashboard month lock)
    funnel: <><path d="M22 3H2l8 9.46V19l4 2v-8.54z" {...p} /></>,
    spark: <><path d="M12 3v6M12 15v6M3 12h6M15 12h6" {...p} /></>,
    bullseye: <><circle cx="12" cy="12" r="9" {...p} /><circle cx="12" cy="12" r="5" {...p} /><circle cx="12" cy="12" r="1.6" fill={color} stroke="none" /></>,
    clock: <><circle cx="12" cy="12" r="8.5" {...p} /><path d="M12 7v5l3 2" {...p} /></>,
    plug: <><path d="M9 3v6M15 3v6M7 9h10v3a5 5 0 0 1-10 0V9ZM12 17v4" {...p} /></>,
    box: <><path d="M3 8 12 3l9 5v8l-9 5-9-5Z" {...p} /><path d="M3 8l9 5 9-5M12 13v8" {...p} /></>,
    sun: <><circle cx="12" cy="12" r="4" {...p} /><path d="M12 2v2M12 20v2M4 12H2M22 12h-2M5.6 5.6 4.2 4.2M19.8 19.8l-1.4-1.4M18.4 5.6l1.4-1.4M4.2 19.8l1.4-1.4" {...p} /></>,
    moon: <><path d="M20 14.5A8 8 0 1 1 9.5 4a6.5 6.5 0 0 0 10.5 10.5Z" {...p} /></>,
    mail: <><rect x="3" y="5" width="18" height="14" rx="2" {...p} /><path d="m3.5 7 8.5 6 8.5-6" {...p} /></>,
    zap: <><path d="M13 2 4 14h7l-1 8 9-12h-7l1-8Z" {...p} /></>,
    dollar: <><path d="M12 2v20M16.5 6.5C16.5 4.6 14.5 3.5 12 3.5S7.5 4.6 7.5 6.8 9.6 10 12 10s4.5 1.3 4.5 3.3-2 3.2-4.5 3.2-4.5-1.1-4.5-3" {...p} /></>,
    dollarslash: <><path d="M12 2v20M16.5 6.5C16.5 4.6 14.5 3.5 12 3.5S7.5 4.6 7.5 6.8 9.6 10 12 10s4.5 1.3 4.5 3.3-2 3.2-4.5 3.2-4.5-1.1-4.5-3" {...p} /><path d="M4 20 20 4" {...p} /></>,
    tag: <><path d="M3 12 12 3h8v8l-9 9-8-8Z" {...p} /><circle cx="16" cy="8" r="1.4" fill={color} stroke="none" /></>,
    store: <><path d="M4 9h16v11H4Z" {...p} /><path d="M4 9 5.5 4h13L20 9" {...p} /><path d="M9 20v-6h6v6" {...p} /></>,
    percent: <><path d="M19 5 5 19" {...p} /><circle cx="7.5" cy="7.5" r="2.3" {...p} /><circle cx="16.5" cy="16.5" r="2.3" {...p} /></>,
    menu: <><path d="M4 7h16M4 12h16M4 17h16" {...p} /></>,
    lock: <><rect x="4.5" y="10" width="15" height="10" rx="2" {...p} /><path d="M8 10V7a4 4 0 0 1 8 0v3" {...p} /></>,
    eye: <><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" {...p} /><circle cx="12" cy="12" r="3" {...p} /></>,
    cart: <><circle cx="9" cy="20" r="1.4" {...p} /><circle cx="17" cy="20" r="1.4" {...p} /><path d="M3 4h2l2.2 11a1 1 0 0 0 1 .8h8.6a1 1 0 0 0 1-.8L20 8H6" {...p} /></>,
    receipt: <><path d="M6 3h12v18l-3-2-3 2-3-2-3 2Z" {...p} /><path d="M9 8h6M9 12h6" {...p} /></>,
    // archive = lidded storage box (distinct from `box`, which is a 3D product cube)
    archive: <><rect x="3" y="4" width="18" height="4.5" rx="1.2" {...p} /><path d="M5 8.5V19a1.5 1.5 0 0 0 1.5 1.5h11A1.5 1.5 0 0 0 19 19V8.5" {...p} /><path d="M10 13h4" {...p} /></>,
    quiz: <><circle cx="12" cy="12" r="8.5" {...p} /><path d="M9.5 9.4a2.6 2.6 0 1 1 3.2 2.5c-.5.2-.7.6-.7 1.1v.6" {...p} /><path d="M12 16.6v.5" {...p} /></>,
    star: <><path d="M12 3.5l2.6 5.3 5.9.9-4.25 4.15 1 5.85L12 17l-5.25 2.75 1-5.85L3.5 9.7l5.9-.9Z" {...p} /></>,
    // App section v2 (Claude Design handoff)
    user: <><circle cx="12" cy="8" r="3.4" {...p} /><path d="M5 20a7 7 0 0 1 14 0" {...p} /></>,
    pill: <><rect x="3" y="8" width="18" height="8" rx="4" {...p} /><path d="M12 8v8" {...p} /></>,
    calendar: <><rect x="3.5" y="5" width="17" height="16" rx="2" {...p} /><path d="M3.5 10h17M8 3v4M16 3v4" {...p} /></>,
    wrench: <><path d="M15.5 4a5 5 0 0 0-5 6.4L4 17v3h3l6.6-6.6A5 5 0 1 0 15.5 4Z" {...p} /></>,
    question: <><circle cx="12" cy="12" r="8.5" {...p} /><path d="M9.6 9.2a2.5 2.5 0 1 1 3.4 2.3c-.6.3-1 .9-1 1.6v.4M12 17v.5" {...p} /></>,
    arrowRight: <><path d="M4 12h15M13 6l6 6-6 6" {...p} /></>,
    phone: <><rect x="6" y="2.5" width="12" height="19" rx="2.5" {...p} /><path d="M10.5 19h3" {...p} /></>,
    info: <><circle cx="12" cy="12" r="8.5" {...p} /><path d="M12 11v5M12 8v.5" {...p} /></>,
    link: <><path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1" {...p} /><path d="M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1" {...p} /></>,
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" style={{ display: 'block', flexShrink: 0, ...style }}>
      {paths[name] || null}
    </svg>
  );
}

// Claude mark — simple sunburst/asterisk built from rotated lines (no illustration)
export function ClaudeMark({ size = 16, color }: any) {
  const t = useT();
  const c = color || t.claude;
  const rays = [0, 45, 90, 135];
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" style={{ display: 'block', flexShrink: 0 }}>
      {rays.map((a) => (
        <line key={a} x1="12" y1="3.5" x2="12" y2="20.5" stroke={c} strokeWidth="2.4"
          strokeLinecap="round" transform={`rotate(${a} 12 12)`} />
      ))}
    </svg>
  );
}

// ---------------- TEXT ----------------
export function Title({ children, size = 22, style = {} }: any) {
  const t = useT();
  return <div style={{ fontFamily: OSWALD, fontWeight: 700, fontSize: size, lineHeight: 1.05, letterSpacing: '0.02em', textTransform: 'uppercase', color: t.fg, ...style }}>{children}</div>;
}
export function Eyebrow({ children, color, style = {} }: any) {
  const t = useT();
  return <div style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 11, letterSpacing: '0.09em', textTransform: 'uppercase', color: color || t.faint, ...style }}>{children}</div>;
}

// ---------------- CARD ----------------
export function Card({ children, pad = 18, style = {}, onClick, hoverable }: any) {
  const t = useT();
  const [h, setH] = React.useState(false);
  return (
    <div onClick={onClick}
      onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{
        background: t.surface, border: '1px solid ' + (hoverable && h ? t.borderStrong : t.border),
        borderRadius: t.cardRadius, padding: pad, boxShadow: t.shadow,
        // minWidth 0: grid/flex items refuse to shrink below their content by
        // default, so a wide table inside a card would push the WHOLE PAGE into
        // horizontal scroll instead of scrolling inside its own overflow-x
        // container. This keeps wide content scrolling in-card.
        minWidth: 0,
        cursor: onClick ? 'pointer' : 'default', transition: 'border-color 160ms, transform 160ms',
        transform: hoverable && h ? 'translateY(-1px)' : 'none', ...style,
      }}>{children}</div>
  );
}

// ---------------- BADGE ----------------
export function Badge({ children, tone = 'neutral', style = {} }: any) {
  const t = useT();
  const map: any = {
    neutral: [t.surface2, t.muted, t.border],
    good: [t.goodSoft, t.good, 'transparent'],
    warn: [t.warnSoft, t.warn, 'transparent'],
    bad: [t.badSoft, t.bad, 'transparent'],
    accent: [t.accentSoft, t.accent, 'transparent'],
    purple: [t.purpleSoft, t.purple, 'transparent'],
    claude: [t.claudeSoft, t.claude, 'transparent'],
  };
  const [bg, fg, bd] = map[tone] || map.neutral;
  return (
    <span style={{
      fontFamily: FIGTREE, fontWeight: 700, fontSize: 10.5, letterSpacing: '0.06em',
      textTransform: 'uppercase', background: bg, color: fg, border: '1px solid ' + bd,
      padding: '3px 8px', borderRadius: 999, display: 'inline-flex', alignItems: 'center', gap: 5,
      whiteSpace: 'nowrap', ...style,
    }}>{children}</span>
  );
}

export function StatusDot({ status, size = 9 }: any) {
  const t = useT();
  const c = status === 'operational' ? t.good : status === 'degraded' ? t.warn : status === 'down' ? t.bad : t.faint;
  return <span style={{ width: size, height: size, borderRadius: 999, background: c, display: 'inline-block', flexShrink: 0, boxShadow: status === 'degraded' ? '0 0 0 3px ' + t.warnSoft : status === 'down' ? '0 0 0 3px ' + t.badSoft : 'none' }} />;
}

// ---------------- BUTTON ----------------
export function Btn({ children, onClick, variant = 'solid', size = 'md', tone = 'dark', icon, style = {}, disabled }: any) {
  const t = useT();
  const [h, setH] = React.useState(false);
  const pad = size === 'sm' ? '7px 12px' : size === 'lg' ? '13px 22px' : '9px 16px';
  const fs = size === 'sm' ? 11.5 : 13;
  let bg, fg, bd;
  const toneColor = tone === 'accent' ? t.accent : tone === 'purple' ? t.purple : tone === 'bad' ? t.bad : t.fg;
  if (variant === 'solid') { bg = disabled ? t.faint : toneColor; fg = tone === 'dark' ? t.surface : '#fff'; bd = 'transparent'; }
  else if (variant === 'soft') { bg = tone === 'accent' ? t.accentSoft : tone === 'purple' ? t.purpleSoft : t.surface2; fg = toneColor; bd = 'transparent'; }
  else { bg = h ? t.surface2 : 'transparent'; fg = t.fg2; bd = t.borderStrong; }
  return (
    <button onClick={disabled ? undefined : onClick} disabled={disabled}
      onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{
        fontFamily: FIGTREE, fontWeight: 700, fontSize: fs, letterSpacing: '0.05em', textTransform: 'uppercase',
        background: bg, color: fg, border: '1px solid ' + bd, borderRadius: 999, padding: pad,
        cursor: disabled ? 'not-allowed' : 'pointer', display: 'inline-flex', alignItems: 'center',
        justifyContent: 'center', gap: 7, opacity: disabled ? 0.6 : 1, filter: h && !disabled ? 'brightness(0.94)' : 'none',
        transition: 'filter 140ms', whiteSpace: 'nowrap', ...style,
      }}>
      {icon && <Icon name={icon} size={fs + 3} />}{children}
    </button>
  );
}

// ---------------- DELTA CHIP (period-over-period %) ----------------
export function Delta({ pct, goodWhenUp = true, size = 12, style = {} }: any) {
  const t = useT();
  if (pct == null) return null;
  const up = pct >= 0;
  const good = goodWhenUp ? up : !up;
  const col = good ? t.good : t.bad;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 1, fontFamily: FIGTREE, fontWeight: 700, fontSize: size, color: col, whiteSpace: 'nowrap', ...style }}>
      {Math.abs(pct)}%<Icon name={up ? 'arrowUp' : 'arrowDown'} size={size + 1} color={col} />
    </span>
  );
}

// ---------------- KPI STAT CARD ----------------
export function Stat({ label, value, sub, delta, deltaGood = true, spark, accent, icon, onClick, right, control }: any) {
  const t = useT();
  return (
    <Card pad={16} onClick={onClick} hoverable={!!onClick} style={{ display: 'flex', flexDirection: 'column', gap: 8, minHeight: 118 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        <Eyebrow style={{ lineHeight: 1.3, flex: '1 1 auto', minWidth: 0 }}>{label}</Eyebrow>
        {!control && <span style={{ flexShrink: 0, display: 'inline-flex' }}>{right ? right : (icon && <Icon name={icon} size={15} color={accent || t.faint} />)}</span>}
        {control && icon && <span style={{ flexShrink: 0, display: 'inline-flex' }}><Icon name={icon} size={15} color={accent || t.faint} /></span>}
      </div>
      {control && <div onClick={(e) => e.stopPropagation()} style={{ display: 'flex' }}>{control}</div>}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ fontFamily: OSWALD, fontWeight: 700, fontSize: 34, lineHeight: 1, letterSpacing: '0.01em', color: accent || t.fg }}>{value}</div>
        {delta != null && <Delta pct={delta} goodWhenUp={deltaGood} />}
      </div>
      {spark && <Sparkline data={spark} color={accent || t.accent} />}
      {sub && <div style={{ fontFamily: FIGTREE, fontSize: 12.5, color: t.muted }}>{sub}</div>}
    </Card>
  );
}

// ---------------- SPARKLINE ----------------
export function Sparkline({ data, color, height = 30 }: any) {
  const t = useT();
  const c = color || t.accent;
  const vals = data.map((d: any) => (typeof d === 'number' ? d : d.value));
  const max = Math.max(...vals), min = Math.min(...vals);
  const range = max - min || 1;
  const W = 120, H = height;
  const pts = vals.map((v: any, i: number) => [(i / (vals.length - 1)) * W, H - ((v - min) / range) * (H - 4) - 2]);
  const d = pts.map((p: any, i: number) => (i ? 'L' : 'M') + p[0].toFixed(1) + ' ' + p[1].toFixed(1)).join(' ');
  const area = d + ` L${W} ${H} L0 ${H} Z`;
  const id = 'sg' + Math.round(min * 1000 + max);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width: '100%', height: H, display: 'block' }}>
      <defs><linearGradient id={id} x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor={c} stopOpacity="0.22" /><stop offset="1" stopColor={c} stopOpacity="0" /></linearGradient></defs>
      <path d={area} fill={`url(#${id})`} />
      <path d={d} fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

// ---------------- AREA / LINE CHART ----------------
export function LineChart({ data, color, height = 150, valueKey = 'value', labelKey = 'date', markerIndex, tickCount = 5 }: any) {
  const t = useT();
  const c = color || t.accent;
  const vals = data.map((d: any) => d[valueKey]);
  const max = Math.max(...vals), min = Math.min(0, ...vals);
  const range = max - min || 1;
  const W = 600, H = height;
  const n = data.length;
  const fx = (i: number) => (n <= 1 ? 0.5 : i / (n - 1));      // 0..1 horizontal
  const fy = (v: number) => 1 - (v - min) / range;             // 0..1 vertical (0 = top)
  const X = (i: number) => fx(i) * W;
  const Y = (v: number) => fy(v) * (H - 6) + 3;
  const line = vals.map((v: any, i: number) => (i ? 'L' : 'M') + X(i).toFixed(1) + ' ' + Y(v).toFixed(1)).join(' ');
  const area = line + ` L${X(n - 1).toFixed(1)} ${H} L${X(0).toFixed(1)} ${H} Z`;
  const id = 'lg' + valueKey + n + Math.round(max);
  let ticks;
  if (n <= 1) ticks = [0];
  else { const k = Math.min(tickCount, n); ticks = [...new Set(Array.from({ length: k }, (_, j) => Math.round(j * (n - 1) / (k - 1))))]; }
  const dot = (i: number, v: number, col: string) => ({ left: fx(i) * 100 + '%', top: (Y(v) / H) * 100 + '%', background: col });
  return (
    <div style={{ width: '100%' }}>
      <div style={{ position: 'relative', width: '100%', height: H }}>
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width: '100%', height: H, display: 'block', overflow: 'visible' }}>
          <defs><linearGradient id={id} x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor={c} stopOpacity="0.20" /><stop offset="1" stopColor={c} stopOpacity="0" /></linearGradient></defs>
          <line x1="0" y1={H - 0.5} x2={W} y2={H - 0.5} stroke={t.border} strokeWidth="1" vectorEffect="non-scaling-stroke" />
          <path d={area} fill={`url(#${id})`} />
          <path d={line} fill="none" stroke={c} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
          {markerIndex != null && <line x1={X(markerIndex)} y1="4" x2={X(markerIndex)} y2={H} stroke={t.bad} strokeWidth="1" strokeDasharray="3 3" vectorEffect="non-scaling-stroke" />}
        </svg>
        {markerIndex != null && <span style={{ position: 'absolute', width: 8, height: 8, borderRadius: 999, transform: 'translate(-50%,-50%)', ...dot(markerIndex, vals[markerIndex], t.bad) }} />}
        <span style={{ position: 'absolute', width: 8, height: 8, borderRadius: 999, transform: 'translate(-50%,-50%)', ...dot(n - 1, vals[n - 1], c) }} />
      </div>
      <div style={{ position: 'relative', height: 18, marginTop: 7 }}>
        {ticks.map((ti) => {
          const tx = ti === 0 ? '0' : ti === n - 1 ? '-100%' : '-50%';
          return <span key={ti} style={{ position: 'absolute', left: fx(ti) * 100 + '%', transform: `translateX(${tx})`, fontFamily: FIGTREE, fontSize: 13, fontWeight: 600, color: t.muted, whiteSpace: 'nowrap' }}>{data[ti][labelKey]}</span>;
        })}
      </div>
    </div>
  );
}

// ---------------- RANGE CONTROLS ----------------
// Segmented control for wide headers (charts). Options default to all 7 ranges.
export function RangePicker({ value, onChange, options }: any) {
  const t = useT();
  const opts = options || BBAdminData.RANGES;
  return (
    <div style={{ display: 'inline-flex', background: t.surface2, border: '1px solid ' + t.border, borderRadius: 999, padding: 2, flexWrap: 'wrap' }}>
      {opts.map((o: any) => {
        const active = value === o.id;
        return (
          <button key={o.id} onClick={() => onChange(o.id)} title={(BBAdminData.RANGE_LONG || {})[o.id] || o.label} style={{
            fontFamily: FIGTREE, fontWeight: 700, fontSize: 11, letterSpacing: '0.04em', textTransform: 'uppercase',
            padding: '5px 10px', borderRadius: 999, border: 'none', cursor: 'pointer',
            background: active ? t.fg : 'transparent', color: active ? t.surface : t.muted, transition: 'background 140ms',
          }}>{o.label}</button>
        );
      })}
    </div>
  );
}

// Compact dropdown for tight spaces (KPI card corners). Custom menu so it themes cleanly.
export function RangeSelect({ value, onChange, options }: any) {
  const t = useT();
  const opts = options || BBAdminData.RANGES;
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<any>(null);
  React.useEffect(() => {
    if (!open) return;
    const h = (e: any) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);
  const cur = opts.find((o: any) => o.id === value) || opts[0];
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={() => setOpen((o) => !o)} style={{
        display: 'inline-flex', alignItems: 'center', gap: 4, background: t.surface2, border: '1px solid ' + t.border, borderRadius: 999,
        padding: '3px 7px 3px 9px', cursor: 'pointer', fontFamily: FIGTREE, fontWeight: 700, fontSize: 10.5, letterSpacing: '0.05em',
        textTransform: 'uppercase', color: t.muted,
      }}>
        {cur.label}<Icon name="chevron" size={11} color={t.faint} style={{ transform: open ? 'rotate(-90deg)' : 'rotate(90deg)' }} />
      </button>
      {open && (
        <div style={{ position: 'absolute', top: '110%', right: 0, zIndex: 30, background: t.surface, border: '1px solid ' + t.borderStrong, borderRadius: t.cardRadius || 6, boxShadow: '0 8px 24px rgba(0,0,0,0.18)', padding: 4, minWidth: 96 }}>
          {opts.map((o: any) => (
            <button key={o.id} onClick={() => { onChange(o.id); setOpen(false); }} style={{
              display: 'flex', alignItems: 'center', width: '100%', textAlign: 'left', gap: 8, padding: '7px 10px', borderRadius: 6, border: 'none', cursor: 'pointer',
              background: o.id === value ? t.surface2 : 'transparent', color: o.id === value ? t.fg : t.fg2,
              fontFamily: FIGTREE, fontWeight: o.id === value ? 700 : 500, fontSize: 12.5, whiteSpace: 'nowrap',
            }}>
              <span style={{ flex: 1 }}>{(BBAdminData.RANGE_LONG || {})[o.id] || o.label}</span>
              {o.id === value && <Icon name="check" size={13} color={t.accent} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------- BUILD NOTE (implementation annotation for the real build) ----------------
export function BuildNote({ children, label = 'For the real build', icon = 'plug', style = {} }: any) {
  const t = useT();
  return (
    <div style={{ display: 'flex', gap: 11, alignItems: 'flex-start', background: t.surface2, border: '1px dashed ' + t.borderStrong, borderRadius: t.cardRadius, padding: '13px 15px', ...style }}>
      <Icon name={icon} size={16} color={t.muted} style={{ marginTop: 2, flexShrink: 0 }} />
      <div>
        <div style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 10.5, letterSpacing: '0.09em', textTransform: 'uppercase', color: t.faint, marginBottom: 4 }}>{label}</div>
        <div style={{ fontFamily: FIGTREE, fontSize: 13, color: t.fg2, lineHeight: 1.55 }}>{children}</div>
      </div>
    </div>
  );
}

// ---------------- CHANNEL CHIP (push / email / both) ----------------
export function ChannelChip({ channel }: any) {
  const t = useT();
  const cfg = ({
    push: { icon: 'bell', label: 'Push', tone: t.purple, bg: t.purpleSoft },
    email: { icon: 'mail', label: 'Email', tone: t.accent, bg: t.accentSoft },
    both: { icon: 'zap', label: 'Push + Email', tone: t.claude, bg: t.claudeSoft },
  } as any)[channel] || { icon: 'bell', label: channel, tone: t.muted, bg: t.surface2 };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: cfg.bg, color: cfg.tone,
      fontFamily: FIGTREE, fontWeight: 700, fontSize: 10.5, letterSpacing: '0.05em', textTransform: 'uppercase',
      padding: '3px 9px', borderRadius: 999, whiteSpace: 'nowrap' }}>
      <Icon name={cfg.icon} size={12} color={cfg.tone} />{cfg.label}
    </span>
  );
}

// ---------------- SECTION TITLE (shared across views; from admin-finance.jsx) ----------------
export function SectionTitle({ children, right, sub }: any) {
  const t = useT();
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, marginBottom: 14 }}>
      <div>
        <Title size={17}>{children}</Title>
        {sub && <div style={{ fontFamily: FIGTREE, fontSize: 12.5, color: t.muted, marginTop: 4 }}>{sub}</div>}
      </div>
      {right}
    </div>
  );
}

// ---------------- TOGGLE (shared; deduped from admin-finance.jsx + admin-views2.jsx) ----------------
export function Toggle({ on, onClick }: any) {
  const t = useT();
  return (
    <button onClick={onClick} style={{ width: 40, height: 23, borderRadius: 999, border: 'none', cursor: 'pointer', background: on ? t.good : t.borderStrong, position: 'relative', flexShrink: 0, transition: 'background 160ms' }}>
      <span style={{ position: 'absolute', top: 3, left: on ? 20 : 3, width: 17, height: 17, borderRadius: 999, background: '#fff', transition: 'left 160ms', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }} />
    </button>
  );
}
